import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "@mantine/core";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import styles from "./styles.module.css";

interface FilterPanelProps {
  /** Called by the reset (clockwise arrow) button */
  onReset: () => void;
  /** Nothing is filtered, so there is nothing to reset */
  resetDisabled?: boolean;
  /** Values of the sections (see FilterSection) that start expanded */
  defaultOpen?: string[];
  /** Cap the height at the viewport and scroll inside the panel. Turn off where the parent already scrolls, e.g. a drawer. */
  scrollable?: boolean;
  /** Placement (width, flex) is up to the page; the box itself is styled here */
  className?: string;
  /** FilterSection items, or any Accordion.Item */
  children: ReactNode;
  "data-testid"?: string;
  resetTestId?: string;
}

/**
 * The filter box shared by the list pages: title and reset button on top, then collapsible sections.
 * Build the sections with FilterSection (and FilterCheckboxList inside them).
 */
export default function FilterPanel({
  onReset,
  resetDisabled = false,
  defaultOpen,
  scrollable = true,
  className,
  children,
  "data-testid": dataTestId,
  resetTestId,
}: FilterPanelProps) {
  const { t } = useTranslation();

  return (
    <div
      className={classNames(styles.panel, scrollable ? styles.scrollable : "", className ?? "")}
      data-testid={dataTestId}
    >
      <div className={styles.header}>
        <Text display="technical" size="sm" tt="uppercase" strong={true}>
          {t("common:filters")}
        </Text>
        <button
          type="button"
          className={styles.reset}
          onClick={onReset}
          disabled={resetDisabled}
          aria-label={t("common:resetFilters")}
          title={t("common:resetFilters")}
          data-testid={resetTestId}
        >
          <SVGAsset name="arrow-clockwise" alt="" width="18" height="18" />
        </button>
      </div>

      {/* Mantine's control is content-box at width 100%, so without border-box its side padding pushes
          the chevron past the panel edge. */}
      <Accordion multiple defaultValue={defaultOpen} classNames={{ control: styles.control }}>
        {children}
      </Accordion>
    </div>
  );
}
